import { FC } from "react";

import {
  FontIcon,
  FontSizes,
  Link,
  Stack,
  Text,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { CommunicationDto } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { useContactsWithDuplicatedMobile } from "@modules/practice/screens/address-book/components/hooks/useContactsWithDuplicatedMobile.tsx";
import { formatCommunication } from "@stores/core/models/Communication.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { CommunicationTypeText } from "@ui-components/RefText.tsx";

import { PreferredArgs, PreferredCheckBox } from "../PreferredCheckBox.tsx";
import { isCommunicationTypePhone } from "./utils.ts";

type ContactMethodListCommunicationsItemProps = {
  isOrganisation?: boolean;
  contact: Contact;
  item: CommunicationDto;
  className?: string;
  onPreferredClick: (preferredArgs: PreferredArgs) => Promise<void>;
  asBullet?: boolean;
};

export const ContactMethodListCommunicationsItem: FC<
  ContactMethodListCommunicationsItemProps
> = ({
  className,
  isOrganisation,
  item,
  onPreferredClick,
  contact,
  asBullet
}) => {
  const { core, practice } = useStores();

  const {
    duplicatesExist,
    duplicatedPatients,
    duplicatedIndividuals,
    duplicatedOrganisations
  } = useContactsWithDuplicatedMobile({
    originalContactId: contact.id,
    type: item.type,
    value: item.value
  });

  const theme = useTheme();

  const duplicatedCombined = [
    ...duplicatedPatients,
    ...duplicatedIndividuals,
    ...duplicatedOrganisations
  ];
  return (
    <Stack
      horizontal
      className={className}
      horizontalAlign="space-between"
      styles={(props, theme) => ({
        root: [
          { padding: "0px 10px" },
          {
            selectors: {
              "&:hover ": {
                backgroundColor:
                  props.className === "noSelect"
                    ? "none"
                    : theme.semanticColors.disabledBackground
              }
            }
          }
        ]
      })}
    >
      <Stack>
        {!asBullet && (
          <Text
            variant="xSmall"
            styles={{ root: { color: theme.palette.neutralSecondary } }}
          >
            <CommunicationTypeText code={item.type} />
          </Text>
        )}
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          {asBullet && (
            <Stack horizontal>
              <FontIcon
                iconName="RadioBullet"
                styles={{
                  root: {
                    display: "block",
                    textAlign: "center",
                    fontSize: FontSizes.size10
                  }
                }}
              />
            </Stack>
          )}
          <Text>{formatCommunication(item, core).value}</Text>
          {duplicatesExist && (
            <TooltipHost
              content={
                <Stack horizontal wrap>
                  <Text
                    styles={{
                      root: {
                        marginRight: 4,
                        fontSize: FontSizes.small
                      }
                    }}
                  >
                    Duplicate number:
                  </Text>
                  {duplicatedCombined.map((contact: Contact, idx) => (
                    <Link
                      styles={{
                        root: { marginRight: 4, fontSize: FontSizes.small }
                      }}
                      key={contact.id}
                      onClick={() => practice.ui.showContactDetails(contact.id)}
                    >
                      {idx !== duplicatedCombined.length - 1
                        ? `${contact.name},`
                        : contact.name}
                    </Link>
                  ))}
                </Stack>
              }
            >
              <FontIcon
                styles={{ root: { color: theme.semanticColors.link } }}
                iconName="PeopleAlert"
              />
            </TooltipHost>
          )}
        </Stack>
      </Stack>
      {!isOrganisation && (
        <Stack verticalAlign="center" styles={{ root: { margin: 0 } }}>
          {isCommunicationTypePhone(item.type) ? (
            <PreferredCheckBox
              contMethodsArgs={{
                type: item.type,
                value: item.value
              }}
              preferred={!!item.preferred}
              tooltipContent={
                item.preferred
                  ? "Preferred contact method"
                  : "Mark as preferred"
              }
              onPreferredClick={onPreferredClick}
            />
          ) : null}
        </Stack>
      )}
    </Stack>
  );
};
